import { flow } from 'es-toolkit';
import PropTypes from 'prop-types';
import { memo, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { mintSingleton } from 'src/client/mint';
import { fetchMe } from '../actions/me';
import withAuth from '../decorators/auth/withAuth';
import withBreakpoints from '../decorators/withBreakpoints';
import withContextProviders from '../decorators/withContextProviders';
import withCurrentRouteCustomProps from '../decorators/withCurrentRouteCustomProps';
import withDesktopApp from '../decorators/withDesktopApp';
import withMobileJourney from '../decorators/withMobileJourney';
import useClientAnalytics from '../hooks/useClientAnalytics';
import useOneTrust from '../hooks/useOneTrust';
import { selectUserAgent } from '../selectors/app';
import connectAsync from '../utils/connectAsync';
import AppRootLoader from './AppRootLoader';
import { DiscordSplash, useDiscord } from './discord';
import MobileWebEntryLoader from './MobileWebEntryLoader';
import withGuideItemProfile from './withGuideItemProfile';

const AppConnectorComponent = memo((props) => {
  const {
    onHistoryUpdate,
    location,
    match,
    route,
    routeProps,
    breakpoint,
    shouldShowMobileOverlay,
    history,
  } = props;
  const userAgent = useSelector(selectUserAgent);
  const MobileWebLandingComponent = useMemo(
    () =>
      routeProps.guideContext.guideId
        ? withGuideItemProfile(MobileWebEntryLoader)
        : MobileWebEntryLoader,
    [routeProps.guideContext.guideId],
  );
  const { isDiscordReady, showDiscordSplashScreen, splashScreenType } = useDiscord(
    history,
    routeProps,
  );
  const contentProps = {
    breakpoint,
    route,
    routeProps,
    location,
    history,
    isDiscordReady,
  };

  if (routeProps.hideAds) {
    mintSingleton.disable();
  }

  useOneTrust({ isMobileBlockerFlow: shouldShowMobileOverlay });
  useClientAnalytics({
    routeProps,
    onHistoryUpdate,
    hasNonAdEligibleOverlay: shouldShowMobileOverlay,
  });

  if (showDiscordSplashScreen) {
    return <DiscordSplash splashScreenType={splashScreenType} />;
  }

  if (shouldShowMobileOverlay) {
    return (
      <MobileWebLandingComponent
        routeProps={routeProps}
        match={match}
        isLanding={routeProps.isLanding}
        location={location}
        userAgent={userAgent}
      />
    );
  }

  return <AppRootLoader {...contentProps} />;
});

AppConnectorComponent.propTypes = {
  location: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  route: PropTypes.object.isRequired,
  routeProps: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
  store: PropTypes.object,
  onHistoryUpdate: PropTypes.func,
  breakpoint: PropTypes.number.isRequired,
  shouldShowMobileOverlay: PropTypes.bool,
};

const AppConnector = flow(
  withMobileJourney,
  withBreakpoints, // IMPORTANT: This needs to render after withCurrentRouteCustomProps
  withCurrentRouteCustomProps,
  withAuth,
  connectAsync([fetchMe]),
  withDesktopApp,
  withContextProviders,
)(AppConnectorComponent);

export default AppConnector;
